import { useState, useEffect } from "react";

/**
 * Hook to track which section is currently visible on screen
 * A section is considered "active" if:
 * - It covers 60% or more of the viewport, OR
 * - 100% of the section is visible on screen
 */
export const useActiveSection = (sectionIds) => {
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      let mostVisibleSection = null;

      sectionIds.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const elementHeight = rect.height;
        const windowHeight = window.innerHeight;

        // Calculate how much of the section is visible in the viewport
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(windowHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const visibilityPercentage = (visibleHeight / elementHeight) * 100;

        // Check if section covers 60% of viewport or is 100% visible
        const coversViewport = (visibleHeight / windowHeight) * 100 >= 60;
        const fullyVisible = visibilityPercentage >= 100;
        const isVisible = coversViewport || fullyVisible;

        if (isVisible) {
          // If multiple sections are visible, prioritize the one with highest visibility
          if (
            !mostVisibleSection ||
            visibleHeight > mostVisibleSection.visibilityPercentage
          ) {
            mostVisibleSection = {
              sectionId,
              isVisible,
              visibilityPercentage: visibleHeight,
            };
          }
        }
      });

      setActiveSection(mostVisibleSection?.sectionId || null);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    // Run once on mount to set initial section
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [sectionIds]);

  return activeSection;
};
